import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import displayOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/DisplayOrder";
import setVmiOrderDetailsPage from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/SetVmiOrderDetailsPage";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors from "@MasterBluePrint/Styles/variables";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let orderNumber;
    let isVmiOrder = false;
    if (location && location.search) {
        const parsedQuery = parseQueryStringCaseInsensitive(location.search, {
            orderNumber: "",
            isVmiOrder: false,
        });
        orderNumber = parsedQuery.orderNumber;
        isVmiOrder = parsedQuery.isVmiOrder;
    }

    const orderState = getOrderState(state, orderNumber);
    const orderStatusMappingDataView = getOrderStatusMappingDataView(state);
    const isOrderNumberStoredInState = state.pages.orderDetails.orderNumber === orderNumber;

    return {
        orderNumber,
        orderState,
        shouldDisplayOrder: !isOrderNumberStoredInState,
        shouldLoadOrderStatusMappings: !orderStatusMappingDataView.value && !orderStatusMappingDataView.isLoading,
        isVmiOrder,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
    };
};

const mapDispatchToProps = {
    displayOrder,
    loadOrderStatusMappings,
    setVmiOrderDetailsPage,
};

export interface OrderDetailsPageStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
    quickLinksGridItem?:GridItemProps;
    mainSectionGridItem?:GridItemProps;
    spinnerWrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
}

export const orderDetailsPageStyles: OrderDetailsPageStyles = {
    spinnerWrapper: {
        css: css`
            position: fixed;
            background: rgba(0, 37, 84, 0.05);
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 99;
            top: 0;
            left: 0;
            svg {
                position: relative;
                z-index: 99;
            }
        `,
    },
    loadingSpinner: {
        css: css`
            margin: auto;
            color: ${themeColors.colorPrimary};
        `,
    },
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: { weight: "bold" },
    quickLinksGridItem: { 
        width: [12, 12, 12, 2, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100%;
                            flex-basis: 100%;
                        `,
                        null,
                        css`
                            background: #fff;
                            padding-left: 0;
                            padding-right: 0;
                            box-shadow: 2px 0 4px 0 rgba(0,37,84,0.05);
                        `,
                        css`
                            max-width: 17rem;
                            flex-basis: 17rem;
                        `,
                        null,
                    ],
                    "min",
                )
            };
            @media screen and (max-width: 1279px) {
                max-width: 100% !important;
                flex-basis: 100% !important;
                background: transparent !important;
                box-shadow: none !important; 
            }
            @media screen and (min-width: 990px) and (max-width: 1199px) {
                max-width: 100%;
                flex-basis: 100%;
            }
            @media screen and (min-width: 990px) and (max-width: 1279px) {
                padding-bottom: 0;
            }
            @media print {
                display: none !important;;
            }
        `,
     },
    mainSectionGridItem: { 
        width: [12, 12, 12, 10, 10],
        css: css`
            position: relative;
            &.mainSectionGridItemContent {
                > div {
                    position: relative;

                    > div:first-child {
                        > div > div > div {
                            h1[data-test-selector="PageTitle"] {
                                @media print {
                                    margin-bottom: 1rem;
                                    
                                    &:after {
                                        display: none;
                                    }
                                }
                                @media print and (orientation: landscape){
                                    margin-bottom: 0.5rem;
                                }
                            }
                        }
                        
                        > div {
                            > div:last-child {
                                > div:first-child {
                                    max-width: 61%;                                    
                                }
                            }
                        }
                    }
                    > div:nth-child(2) {
                        > div {
                            > div:first-child {
                                > div {
                                    > div:first-child {
                                        @media print and (orientation: portrait){
                                            display: none;
                                        }
                                        @media print and (orientation: landscape){
                                            display: none;
                                        }
                                    }
                                    > div {
                                        > div {
                                            > div {
                                                > div {
                                                    > div:first-child {
                                                        border-bottom: 1px solid #C2D1D9;
                                                        border-top: 1px solid #C2D1D9;
                                                        padding-top: 15px;
                                                        margin-bottom: 1.25rem;

                                                        ${({ theme }: { theme: BaseTheme }) =>
                                                                breakpointMediaQueries(
                                                                    theme,
                                                                    [
                                                                        null,
                                                                        css`
                                                                            text-align: center;

                                                                            > div {
                                                                                > div[class^='GridItemStyle'], div[class*='GridItemStyle'] {
                                                                                    @media print {
                                                                                        padding-top: 0 !important;
                                                                                    }
                                                                                    &:first-child {
                                                                                        padding-top: 1.25rem;
                                                                                    }
                                                                                    &:last-child {
                                                                                        padding-bottom: 1.25rem;
                                                                                    }
                                                                                    > div {
                                                                                        > div {
                                                                                            padding-bottom: 0;
                                                                                            @media print {
                                                                                                padding-bottom: 0.3125rem;
                                                                                            }
                                                                                            
                                                                                            h3[class^='TypographyStyle'],
                                                                                            h3[class*='TypographyStyle'],
                                                                                            span[class^='TypographyStyle'],
                                                                                            span[class*='TypographyStyle']{
                                                                                                margin-bottom: 0;
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }
                                                                        `,
                                                                    ],
                                                                    "max",
                                                                )}

                                                        
                                                    }
                                                    > div:nth-child(2) {
                                                        margin-bottom: 0.75rem;
                                                        > div {
                                                            > div:first-child {
                                                                > div {
                                                                    background: #FFFFFF;
                                                                    padding: 0.9375rem 1.5625rem 1.5625rem;
                                                                }                                                                

                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                breakpointMediaQueries(
                                                                    theme,
                                                                    [
                                                                        null,
                                                                        null,
                                                                        null,
                                                                        css`
                                                                            margin-right: 0.9375rem;
                                                                            max-width: 43%; 
                                                                        `,
                                                                    ],
                                                                    "min",
                                                                )}                                                
                                                                                                                            
                                                            }
                                                            > div:nth-child(2) {
                                                                padding: 0.9375rem 0.2rem 0.4688rem .5625rem;
                                                                                                                              
                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                breakpointMediaQueries(
                                                                    theme,
                                                                    [
                                                                        null,
                                                                        css`
                                                                            padding-top: 1.25rem;
                                                                            max-width: 50%;
                                                                        `,
                                                                    ],
                                                                    "max",
                                                                )}

                                                            }
                                                            > div:last-child {
                                                                padding: 0.9375rem 0.8rem 0.4688rem 1.125rem;

                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                breakpointMediaQueries(
                                                                    theme,
                                                                    [
                                                                        null,
                                                                        css`
                                                                            padding-top: 1.25rem;
                                                                            max-width: 50%;
                                                                        `,
                                                                    ],
                                                                    "max",
                                                                )}
                                                            }
                                                        }
                                                    }
                                                    > div:last-child {
                                                        padding: 0.625rem;
                                                        border-radius: 2px;
                                                        background-color: #F0F2F3;
                                                    }

                                                }
                                            }

                                        }
                                    }
                                }
                                @media print {
                                    width: 100%;
                                    max-width: 100%;
                                }
                            }
                            
                            > div:last-child {
                                > div {
                                    background: #fff;
                                    padding: 20px;
                                    box-shadow: 0 2px 4px 4px rgba(0,0,0,0.03);
	                                border-radius: 2px;
                                }
                                ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                            // position: absolute;
                                            // bottom: 0;
                                        `,
                                    ],
                                    "max",
                                )}
                                @media print {
                                    width: 50%;
                                    max-width: 50%;
                                }
                            }
                        }
                    }
                }
            }
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100%;
                            flex-basis: 100%;
                        `,
                        null,
                        null,
                        css`
                            max-width: calc(100% - 17.5rem);
                            flex-basis: calc(100% - 17.5rem);
                            padding-left: 1.5625rem;
                        `,
                        null,
                    ],
                    "min",
                )
            };
            @media print {
                max-width: 100%;
                width: 100%
            }
            @media screen and (min-width: 990px) and (max-width: 1199px) {
                max-width: 100%;
                flex-basis: 100%;
            }
            @media screen and (max-width: 1279px) {
                max-width: 100% !important;
                flex-basis: 100% !important;

                .totalReorderbtnWrapper {
                    display: none;
                }
            }
        `,
    },
};

type Props = ResolveThunks<typeof mapDispatchToProps> & PageProps & ReturnType<typeof mapStateToProps>;

class OrderDetailsPage extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        // The share entity (for orders) functionality can use this page
        // to render HTML for PDF generation, so we need to leave this SSR
        // ability so that functionality works.
        if (this.props.shouldLoadOrderStatusMappings) {
            this.props.loadOrderStatusMappings();
        }
        if (this.props.orderNumber && this.props.shouldDisplayOrder) {
            this.props.displayOrder({
                orderNumber: this.props.orderNumber,
                expand: this.props.isVmiOrder ? ["orderLines", "shipments", "vmidetails"] : ["orderLines", "shipments"],
            });
        }
        if (this.props.isVmiOrderDetailsPage) {
            this.props.setVmiOrderDetailsPage(false);
        }
    }

    render() {
        const styles = orderDetailsPageStyles;
        return (
            <Page data-test-selector="orderDetail">
                {this.props.orderState.errorStatusCode === 404 ? (
                    <StyledWrapper {...styles.loadFailedWrapper}>
                        <Typography {...styles.loadFailedText}>{siteMessage("Order_NotFound")}</Typography>
                    </StyledWrapper>
                ) : (
                    <>
                        <GridContainer>
                            <GridItem {...styles.quickLinksGridItem}>
                                <Zone contentId={this.props.id} zoneName="NavigationList" />
                            </GridItem>
                            <GridItem {...styles.mainSectionGridItem} className="mainSectionGridItemContent">
                           {(this.props.orderState.isLoading)? (
                                <StyledWrapper {...styles.spinnerWrapper} data-test-selector="orderSpinnerLoading">
                                    <LoadingSpinner {...styles.loadingSpinner}/>
                                </StyledWrapper>
                           ) : (                 
                                <OrderStateContext.Provider value={this.props.orderState}>
                                    <Zone contentId={this.props.id} zoneName="Content"/>
                                </OrderStateContext.Provider>
                            )}  
                            </GridItem>
                        </GridContainer>
                    </>
                )}
                <Modals />
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "OrderDetailsPage" instead of this constant.
 */
export const OrderDetailsPageContext = "OrderDetailsPage";
